import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import MusaCard from "@/components/MusaCard";
import { playElegantClick } from "@/lib/sound";
import { Search, X, TrendingUp, Crown } from "lucide-react";
import { MX_STATE_NAMES, citiesForState } from "@/lib/mexicoLocations";

export default function Gallery() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [topWeek, setTopWeek] = useState([]);

  const city = params.get("city") || "";
  const stateName = params.get("state") || "";
  const vipOnly = params.get("vip") === "1";
  const category = params.get("category") || "";

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const query = {};
    if (city) query.city = city;
    if (stateName) query.state = stateName;
    if (vipOnly) query.vip_only = true;
    if (q) query.q = q;
    if (category) query.category = category;
    query.limit = 60;
    const { data } = await api.get("/profiles", { params: query });
    setItems(data.items || []);
    setTotal(data.total || 0);
    setLoading(false);
  }, [city, stateName, vipOnly, q, category]);

  useEffect(() => { api.get("/profiles/categories").then((r) => setCategoryGroups(r.data || [])); }, []);
  useEffect(() => { api.get("/profiles/top-week").then((r) => setTopWeek(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Cities available for chosen state
  const availableCities = useMemo(() => stateName ? citiesForState(stateName) : [], [stateName]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    // If state changes, drop the city filter so the user starts fresh.
    if (key === "state") p.delete("city");
    setParams(p);
  };

  const clearFilters = () => { setParams({}); setQ(""); };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="gallery-page">
      <div className="mb-12 fade-up">
        <div className="overline mb-3">{t("gallery.directory_overline")}</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light" style={{ color: "var(--text-primary)" }}>
          {city ? city : t("gallery.all_musas")}
        </h1>
        <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
          {loading ? t("common.loading") : t(total === 1 ? "gallery.results_count_one" : "gallery.results_count_other", { count: total, vip: vipOnly ? t("gallery.vip_word") : "" })}
        </p>
      </div>

      {/* Filter bar */}
      <div
        className="flex flex-wrap items-center gap-3 py-5 mb-10"
        style={{ borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }}
        data-testid="filter-bar"
      >
        <div className="flex items-center gap-2 flex-1 min-w-[260px]" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", padding: "0.3rem 1rem" }}>
          <Search size={14} strokeWidth={1.3} color="var(--brand-primary)" />
          <input
            placeholder="Buscar por nombre, ID, teléfono, @telegram, ciudad…"
            className="bg-transparent outline-none text-sm w-full py-2"
            style={{ color: "var(--text-primary)" }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="filter-search"
          />
        </div>

        <select
          className="input-field max-w-[180px]"
          value={stateName}
          onChange={(e) => setParam("state", e.target.value)}
          data-testid="filter-state"
        >
          <option value="">Todos los estados</option>
          {MX_STATE_NAMES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          className="input-field max-w-[180px]"
          value={city}
          onChange={(e) => setParam("city", e.target.value)}
          data-testid="filter-city"
          disabled={!stateName && availableCities.length === 0}
          title={!stateName ? "Selecciona primero un estado" : undefined}
        >
          <option value="">{stateName ? "Todas las ciudades" : t("gallery.all_cities")}</option>
          {availableCities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-xs cursor-pointer px-3" style={{ color: vipOnly ? "var(--vip-gold)" : "var(--text-secondary)" }} data-testid="filter-vip-label filter-vip">
          <input
            type="checkbox"
            checked={vipOnly}
            onChange={(e) => setParam("vip", e.target.checked ? "1" : "")}
            data-testid="filter-vip-checkbox"
          />
          {t("gallery.only_vip")}
        </label>

        {(city || stateName || vipOnly || q || category) && (
          <button className="btn-ghost flex items-center gap-2 text-xs" onClick={clearFilters} data-testid="filter-clear">
            <X size={12} /> {t("gallery.clear_short")}
          </button>
        )}
      </div>

      {/* Top of the Week */}
      {topWeek.length > 0 && !category && !q && !city && !vipOnly && (
        <div className="mb-12" data-testid="top-week-section">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div className="inline-flex items-center gap-3">
              <TrendingUp size={18} color="var(--vip-gold)" />
              <h2 className="font-serif text-2xl md:text-3xl font-light" style={{ color: "var(--text-primary)" }}>
                {t("gallery.top_week_title")} <em style={{ color: "var(--vip-gold)", fontStyle: "italic" }}>{t("gallery.top_week_emph")}</em>
              </h2>
            </div>
            <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
              {t("gallery.top_week_subtitle")}
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-3" style={{ scrollbarWidth: "thin" }} data-testid="top-week-row">
            {topWeek.slice(0, 10).map((p, i) => {
              const cover = p.hero_photo || (p.photos && p.photos[0]) || "";
              return (
                <Link
                  key={p.id}
                  to={`/musa/${p.slug || p.id}`}
                  data-no-sound
                  onClick={() => playElegantClick(i / 10)}
                  className="relative flex-shrink-0 group"
                  style={{ width: 180, height: 240, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}
                  data-testid={`top-week-card-${i + 1}`}
                >
                  {cover ? (
                    <img src={cover} alt={p.stage_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--text-muted)" }}>
                      {t("gallery.no_photo")}
                    </div>
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,7,8,0.95) 0%, rgba(8,7,8,0.2) 50%, transparent 100%)" }} />
                  <span
                    className="absolute top-2 left-2 font-serif"
                    style={{
                      fontSize: 36, lineHeight: 1, color: i < 3 ? "var(--vip-gold)" : "#fff",
                      textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                      fontWeight: 600,
                    }}
                  >
                    {i + 1}
                  </span>
                  {p.is_vip && (
                    <Crown size={14} color="var(--vip-gold)" className="absolute top-3 right-3" />
                  )}
                  <div className="absolute bottom-2 left-3 right-3">
                    <p className="font-serif text-base leading-tight" style={{ color: "#fff" }}>
                      {p.stage_name}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.7)" }}>
                      {p.city}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Category pills — horizontally scrollable */}
      {categoryGroups.length > 0 && (
        <div
          className="flex gap-2 mb-8 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "thin" }}
          data-testid="gallery-category-pills"
        >
          <button
            onClick={() => setParam("category", "")}
            className="text-xs whitespace-nowrap px-3 py-2 transition-colors"
            style={{
              background: !category ? "var(--brand-primary)" : "transparent",
              color: !category ? "#fff" : "var(--text-secondary)",
              border: `1px solid ${!category ? "var(--brand-primary)" : "var(--border-subtle)"}`,
            }}
            data-testid="category-pill-all"
          >
            {t("gallery.categories_all")}
          </button>
          {categoryGroups.flatMap((g) => g.items).filter((c) => c.count > 0).map((c) => (
            <button
              key={c.slug}
              onClick={() => setParam("category", category === c.slug ? "" : c.slug)}
              className="text-xs whitespace-nowrap px-3 py-2 transition-colors"
              style={{
                background: category === c.slug ? "var(--brand-primary)" : "transparent",
                color: category === c.slug ? "#fff" : "var(--text-secondary)",
                border: `1px solid ${category === c.slug ? "var(--brand-primary)" : "var(--border-accent)"}`,
              }}
              data-testid={`category-pill-${c.slug}`}
            >
              {c.label} <span style={{ color: category === c.slug ? "rgba(255,255,255,0.7)" : "var(--text-muted)" }}>({c.count})</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="shimmer" style={{ aspectRatio: "3/4", background: "var(--bg-surface)" }} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-24" data-testid="gallery-empty">
          <p style={{ color: "var(--text-secondary)" }}>{t("gallery.no_results")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="gallery-grid">
          {items.map((p, i) => <MusaCard key={p.id} profile={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
